import { currencyMeta, formatHuman, parse, type Money as MoneyValue } from '@fint/money';
import { cn } from '@/lib/cn';

interface MoneyProps {
  /** A decimal string from the API. Never a float. */
  value?: string | number | null;
  /**
   * A Money value that has already been computed - from the live totals in the
   * composer, say.
   *
   * Preferred over `value` wherever one is to hand. The alternative was callers
   * writing `minor / 100`, which is wrong for JPY (no minor unit) and KWD
   * (three), and wrong SILENTLY - it renders a plausible number.
   */
  money?: MoneyValue;
  currency: string;
  className?: string;
  /** Colour negatives red. Off by default - a credit note is not an error. */
  colorNegative?: boolean;
  /** Drop the currency symbol, for a column that already has one in its header. */
  showSymbol?: boolean;
}

/**
 * The only way money is rendered.
 *
 * Three things it guarantees that an inline `.toFixed(2)` does not:
 *
 *   1. Locale-correct grouping. INR groups as 5,00,000 - not 500,000 - and an
 *      Indian business reading the wrong grouping has to count digits.
 *   2. The right number of decimals for the currency. JPY has none; showing
 *      ¥1,000.00 is wrong, not merely ugly.
 *   3. Tabular figures, so a column lines up on the decimal point.
 *
 * The value crosses the wire as a decimal string and is parsed by @fint/money,
 * so it never passes through a float on the way to the screen.
 */
export function Money({ value, money: given, currency, className, colorNegative = false, showSymbol = true }: MoneyProps) {
  let money: MoneyValue;
  try {
    money = given ?? parse(value, currency);
  } catch {
    // An unparseable amount or an unknown currency. Showing the raw value is
    // more honest than showing 0.00, which looks like a real balance.
    return <span className={cn('tabular text-muted-foreground', className)}>{String(value ?? '—')}</span>;
  }

  const negative = money.minor < 0;
  return (
    <span
      className={cn('tabular', negative && colorNegative && 'text-destructive', className)}
      title={`${currencyMeta(currency).name}`}
    >
      {formatHuman(money, { showSymbol })}
    </span>
  );
}
